import React, { useEffect, useRef, useState } from "react";
import { handleCopy, handleLike, handleSpeak } from "../utils/chatUtils";
import { postMessage } from "../services/api";
import { selectMessages } from "../store/message/selectors";
import { setMessages, removeLastMessage } from "../store/message/slice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Markdown, { Components } from 'react-markdown'
import Modal from 'react-modal';

import chatIcon from "../images/chat-icon-new.png";
import Copy from "../images/copy.svg"
import Player from "../images/player.png";
import Speaker from "../images/volume.svg"
import ThumbsUp from "../images/thumbs-up.svg"
import ThumbsDown from "../images/thumbs-down.svg"

import "react-toastify/dist/ReactToastify.css";
import ChatLanguage from "./CommonComponents/ChatLanguage";
import ChatFontSize from "./CommonComponents/ChatFontSize";

export interface MessageType {
  type: string;
  msg: string;
  datetime: string;
}

const MessageInput: React.FC = () => {

  const dispatch = useDispatch();
  const [inputState, setInputState] = useState<boolean>(true);
  const [input, setInput] = useState<boolean>(false);
  const [message, setMessage] = useState("");
  const bodyRef = useRef<HTMLInputElement | null>(null);
  const messages = useSelector(selectMessages);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkTarget, setLinkTarget] = useState('');
  const [language, setLanguage] = useState('English');
  const [fontSize, setFontSize] = useState('15px');
  const imgSize = `${1.5 * parseInt(fontSize)}px`;
  const marginLeftRight = `${parseInt(fontSize) / 4}px`;
  const marginTopBottom = `${parseInt(fontSize) / 1.5}px`;
  const HeaderText = messages[0]?.msg

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setLinkTarget(href);
    setShowLinkModal(true);
  };

  const handleLinkConfirm = (): void => {
    window.open(linkTarget, '_blank');
    setShowLinkModal(false);
  };

  const customComponents: Components = {
    p: ({ children }) => <p style={{ fontSize }}>{children}</p>,
    h1: ({ children }) => <h1 style={{ fontSize }}>{children}</h1>,
    h2: ({ children }) => <h2 style={{ fontSize }}>{children}</h2>,
    h3: ({ children }) => <h3 style={{ fontSize }}>{children}</h3>,
    li: ({ children }) => <li style={{ fontSize }}>{children}</li>,
    ul: ({ children }) => <ul style={{ fontSize }}>{children}</ul>,
    ol: ({ children }) => <ol style={{ fontSize }}>{children}</ol>,
    em: ({ children }) => <em style={{ fontSize }}>{children}</em>,
    strong: ({ children }) => <strong style={{ fontSize }}>{children}</strong>,
    a: ({ node, href, children, ...props }) => (
      <a
        href={href}
        onClick={(e) => href && handleLinkClick(e, href)}
        style={{ fontSize, color: 'blue', textDecoration: 'underline' }}
        {...props}
      >
        {children}
      </a>
    )
  };
  

  const scrollToElement = () => {
    setInput(false)
    if (bodyRef.current) { bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }
  };

  useEffect(() => { scrollToElement(); }, [messages]);

  useEffect(() => {
    const handleKeyPress = async (event: any) => {
      if (event.key === 'Enter' && message.trim() !== "") {
        if (inputState === true) {
          if (!message.trim()) {
            toast.warning("Please enter a valid input!", { position: "top-right", autoClose: 3000 });
            return;
          }
          setInputState(false)
          const currentTime = new Date().toISOString();
          dispatch(setMessages({ type: "user", msg: message, datetime: currentTime }));
          setInput(true)
          dispatch(setMessages({ type: "bot", msg: "Thinking...", datetime: currentTime }));
          try {
            setMessage("");
            const response = await postMessage("/api/message", { message, language }, true);
            if (response.error) { toast.warning(response.data.bot_response, { position: "top-right", autoClose: 3000 }); }
            dispatch(removeLastMessage());
            dispatch(setMessages({ type: "bot", msg: response.data.bot_response, datetime: currentTime }));
          }
          catch { toast.error("Failed to send message. Please try again later.", { position: "top-right", autoClose: 3000 }); }
          finally { setInputState(true) }
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [message, language, inputState, dispatch]);

  const handleSendMessage = async () => {
    if (inputState === true) {
      if (!message.trim()) {
        toast.warning("Please enter a valid input!", { position: "top-right", autoClose: 3000 });
        return;
      }
      setInputState(false)
      const currentTime = new Date().toISOString();
      dispatch(setMessages({ type: "user", msg: message, datetime: currentTime }));
      setInput(true)
      dispatch(setMessages({ type: "bot", msg: "Thinking...", datetime: currentTime }));
      try {
        setMessage("");
        const response = await postMessage("/api/message", { message, language }, true);
        if (response.error) { toast.warning(response.data.bot_response, { position: "top-right", autoClose: 3000 }); }
        dispatch(removeLastMessage());
        dispatch(setMessages({ type: "bot", msg: response.data.bot_response, datetime: currentTime }));
      }
      catch { toast.error("Failed to send message. Please try again later.", { position: "top-right", autoClose: 3000 }); }
      finally { setInputState(true) }
    }
  };

  return (
    <>
      <div className="chat-right_sidebar">
        <div className="chat-body-header">
          <div className="chat-title">
            <h1>{HeaderText}</h1>
          </div>
          <div className="msg-opt">
            <ChatLanguage language={language} setLanguage={setLanguage} />
            <ChatFontSize size={fontSize} setSize={setFontSize} />
            <div className="chat-body-top">
              <img src={Player} alt="Player" />
            </div>
          </div>

        </div>

        <div className="chat-message-box">
          <div ref={bodyRef} className="chat-wrapper h-cal">
            {messages.map((message: MessageType, index: number) => {
              const { type, msg, datetime } = message;
              const isBotMessage = type === "bot";

              return (
                <div key={`${type}-${index}`} className={isBotMessage ? "chat-message-left" : "chat-message-right"}>
                  {isBotMessage ? (
                    <>
                      <div style={{ alignSelf: "start", margin: `${marginTopBottom} ${marginLeftRight}` }}>
                        <img className="chat-profile-img" src={chatIcon} alt="Profile" style={{ width: `${imgSize}`, height: `${imgSize}` }} />
                      </div>
                      <div className="chat-message-text">
                        <strong style={{ fontSize }}>Wellie:</strong><Markdown components={customComponents}>{msg}</Markdown>
                        <div className="chat-message-insert">
                          <div onClick={() => handleSpeak(msg, datetime + "speak")}><img className="speak" id={`${datetime}speak`} src={Speaker} alt="speak" /></div>
                          <div onClick={() => handleCopy(msg, datetime + "copy")}><img id={`${datetime}copy`} src={Copy} alt="Copy" /></div>
                          <div onClick={() => handleLike(datetime, "like")}><img id={`${datetime}like`} src={ThumbsUp} alt="Like" /></div>
                          <div onClick={() => handleLike(datetime, "unlike")}><img id={`${datetime}unlike`} src={ThumbsDown} alt="Unlike" /></div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="chat-messagedark">
                      <p style={{ fontSize: fontSize }}>{msg}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Modal
          isOpen={showLinkModal}
          onRequestClose={() => setShowLinkModal(false)}
          contentLabel="Link Confirmation"
          style={{
            content: {
              top: '50%',
              left: '50%',
              right: 'auto',
              bottom: 'auto',
              marginRight: '-50%',
              transform: 'translate(-50%, -50%)',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '400px'
            },
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }}
        >
          <h3>External Link</h3>
          <p>Are you sure you want to navigate to this link?</p>
          <p style={{ wordBreak: 'break-all', fontSize: '14px', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
            {linkTarget}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button onClick={() => setShowLinkModal(false)} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #ccc' }}>
              Cancel
            </button>
            <button onClick={handleLinkConfirm} style={{ padding: '8px 16px', borderRadius: '4px', backgroundColor: '#007bff', color: '#fff', border: 'none' }}>
              Proceed
            </button>
          </div>
        </Modal>

        <div className="chat-message-send">
          <input type="text" placeholder="Message AskWellie" value={message} onChange={(e) => setMessage(e.target.value)} key={input ? "disabled" : "enabled"} disabled={input} />
          <div className="send-message" onClick={handleSendMessage}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="24" height="24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
            </svg>
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageInput;
